import React, { useEffect, useState } from 'react'
import { useI18n } from '../../../i18n/I18nContext'
import { getGlobalPlatformAdministration, grantGlobalPlatformPermission, revokeGlobalPlatformPermission, type GlobalPlatformAdministration } from '../../../services/platformAuthority'

export const GlobalPlatformPermissionsPanel: React.FC<{ userId: string }> = ({ userId }) => {
  const { t } = useI18n()
  const [data, setData] = useState<GlobalPlatformAdministration | null>(null)
  const [reason, setReason] = useState('')
  const [expires, setExpires] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  useEffect(() => {
    let current = true
    setData(null); setReason(''); setExpires(''); setNotice(''); setError('')
    getGlobalPlatformAdministration(userId).then(result => { if (current) setData(result) }).catch(failure => {
      if (current) setError(failure.response?.data?.message || failure.response?.data?.error?.message || t('Unable to load global platform permissions.'))
    })
    return () => { current = false }
  }, [userId, t])
  const change = async (permissionKey: string, revoke: boolean) => {
    if (busy || reason.trim().length < 5) return
    setBusy(true); setError(''); setNotice('')
    try {
      const result = revoke
        ? await revokeGlobalPlatformPermission(userId, permissionKey, reason.trim())
        : await grantGlobalPlatformPermission(userId, permissionKey, reason.trim(), expires ? new Date(expires).toISOString() : null)
      setData(await getGlobalPlatformAdministration(userId))
      setNotice(t(result.data.changed ? 'Global permission updated. The account owner must sign in again.' : 'Global permission already matches this request.'))
    } catch (failure: any) {
      setError(failure.response?.data?.message || failure.response?.data?.error?.message || t('Unable to update global platform permissions.'))
    } finally { setBusy(false) }
  }
  if (!data) return <p role={error ? 'alert' : 'status'}>{error ? t(error) : t('Loading...')}</p>
  const systemIdentity = data.target.system_role === 'system_super_admin'
  return <section className="stack" data-testid="global-platform-permissions">
    <p>{t('These permissions apply across tenants and require MFA. Tenant roles do not grant them.')}</p>
    <p><strong>{data.target.email}</strong></p>
    {systemIdentity ? <p>{t('System administrators already have global platform authority.')}</p> : <>
      <label htmlFor="global-permission-reason">{t('Reason for global permission change')}</label>
      <input id="global-permission-reason" className="input" value={reason} onChange={event => setReason(event.target.value)} minLength={5} maxLength={500} disabled={busy} />
      <label htmlFor="global-permission-expiry">{t('Optional global permission expiry')}</label>
      <input id="global-permission-expiry" className="input" type="datetime-local" value={expires} onChange={event => setExpires(event.target.value)} disabled={busy} />
    </>}
    {error && <p role="alert" className="error-text">{t(error)}</p>}
    {notice && <p role="status">{notice}</p>}
    <div className="table-wrapper"><table className="data-table"><thead><tr><th>{t('Permission')}</th><th>{t('Status')}</th><th>{t('Actions')}</th></tr></thead><tbody>
      {data.catalog.map(permission => {
        const grant = data.grants.find(item => item.permission_key === permission.permission_key)
        const active = Boolean(grant && !grant.revoked_at && (!grant.expires_at || new Date(grant.expires_at).getTime() > Date.now()))
        return <tr key={permission.permission_key}><td><strong>{t(permission.label)}</strong><p className="text-muted">{t(permission.description)}</p></td>
          <td>{t(systemIdentity || active ? 'Active' : 'Not granted')}</td>
          <td>{!systemIdentity && <button type="button" className="btn btn-ghost" disabled={busy || reason.trim().length < 5 || (!active && (!data.target.active || data.target.status !== 'active'))} onClick={() => void change(permission.permission_key, active)}>{t(active ? 'Revoke global permission' : 'Grant global permission')}</button>}</td></tr>
      })}
    </tbody></table></div>
  </section>
}
